import React, { useEffect, useState, useMemo } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { useTerminologyConfig } from 'context/DataContext';

import Modal from 'components/molecules/modal';
import {
  ModalContent,
  Label,
  ButtonContainer,
  SimpleTable,
  ResultsContainer,
  LabelIconWrapper,
  LoadingContainer,
  CreateHelperText,
  ThirdPartySearchContainer,
  LinkageTableInput,
} from './LinkThirdpartyModal.styles';
import { useConfig } from 'config';
import { useAuth } from 'auth';
import { Button, FormControl, Loading, Tooltip, Typography } from 'components/atoms';
import Input from 'components/atoms/field-input';
import { useDebounce, useFetch } from 'hooks';
import ThirdPartiesSearchResultList from './search-results-list/ThirdPartiesSearchResultList';
import { ThirdPartyBasicData } from '../types';
import LabelIcon from 'components/molecules/label-icon';
import { Icon } from 'components/atoms';
import { ReactComponent as UnknownTypeIcon } from 'assets/svg/Icons/Unknown.svg';
import {
  LinkedThirdPartyFieldsConfigs,
  ThirdPartyCreateModelResponse,
  ThirdPartyType,
} from 'types';
import { ThirdPartyFormModal } from 'components/organisms/third-party-form-modal';
import { LinkedThirdParty } from 'pages/initiate-due-diligence/types';
import PercentageInput from 'components/atoms/percentage-input';

type LinkThirdpartyModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  onLinked?: (linkedThirdParty: LinkedThirdParty) => void;
  title: string;
  mainThirdparty: ThirdPartyBasicData;
  mainThirdPartyId: string;
  activeThirdPartyInternalId?: string | null;
  linkedThirdPartyConfigurations: LinkedThirdPartyFieldsConfigs;
};

const LinkThirdpartyModal: React.FC<LinkThirdpartyModalProps> = ({
  isOpen,
  onClose,
  onLinked,
  title,
  mainThirdparty,
  mainThirdPartyId,
  activeThirdPartyInternalId = null,
  linkedThirdPartyConfigurations,
}) => {
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi } = useConfig();
  const { tokenInfo } = useAuth();
  const [thirdpartiesData, setThirdpartiesData] = useState([]);
  const [activeInternalId, setActiveInternalId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedThirdparty, setSelectedThirdparty] = useState<ThirdPartyBasicData | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const {
    data: thirdPartySearchData,
    loading: thirdPartySearchLoading,
    error: thirdPartySearchError,
    trigger: thirdPartySearchTrigger,
  } = useFetch(`${vantageWebApi}/thirdParty/search-available-linkage`, {
    method: 'POST',
    lazy: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
  });

  const { trigger: linkThirdPartyTrigger, loading: linkThirdPartyLoading } = useFetch(
    `${vantageWebApi}/thirdParty/${mainThirdPartyId}/link/:selectedThirdPartyId`,
    {
      method: 'POST',
      lazy: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  const searchThirdpartyForm = useForm({
    mode: 'all',
    defaultValues: {
      searchParam: null,
    },
  });

  const {
    watch: watchSearch,
    register: registerSearch,
    reset: resetThirdpartySearch,
    setValue: setSearchValue,
    formState: { touchedFields: isTouchedSearchField },
  } = searchThirdpartyForm;

  function getSchema(selectedType: string, config: LinkedThirdPartyFieldsConfigs) {
    let requireRelation = false;
    let requirePercent = false;

    switch (selectedType) {
      case 'Individual':
        requireRelation = config?.isIndividualRelationshipTypeMandatory;
        requirePercent =
          config?.isIndividualOwnershipPercentageMandatory &&
          config?.isIndividualOwnershipPercentageEnabled;
        break;

      case 'Business':
        requireRelation = config?.isCompanyRelationshipTypeMandatory;
        requirePercent =
          config?.isCompanyOwnershipPercentageMandatory &&
          config?.isCompanyOwnershipPercentageEnabled;
        break;

      case 'Unknown':
        requireRelation = config?.isUnknownRelationshipTypeMandatory;
        requirePercent =
          config?.isUnknownOwnershipPercentageMandatory &&
          config?.isUnknownOwnershipPercentageEnabled;
        break;
    }

    return yup.object({
      relationship1to2: yup
        .string()
        .nullable()
        .when([], {
          is: () => requireRelation,
          then: (schema) => schema.required(),
        }),
      relationship2to1: yup
        .string()
        .nullable()
        .when([], {
          is: () => requireRelation,
          then: (schema) => schema.required(),
        }),
      percentage: yup
        .number()
        .nullable()
        .when([], {
          is: () => requirePercent,
          then: (schema) => schema.required().min(0).max(100),
        }),
    });
  }

  const selectedType = selectedThirdparty?.type;

  const schema = useMemo(
    () => getSchema(selectedType, linkedThirdPartyConfigurations),
    [selectedType, linkedThirdPartyConfigurations]
  );

  function getConfigValue(config, type, key) {
    if (type === 'Business') return config?.[`isCompany${key}`];
    if (type === 'Individual') return config?.[`isIndividual${key}`];
    if (type === 'Unknown') return config?.[`isUnknown${key}`];
    return false;
  }

  const isPercentageEnabled = getConfigValue(
    linkedThirdPartyConfigurations,
    selectedThirdparty?.type,
    'OwnershipPercentageEnabled'
  );
  const isRelationshipRequired = getConfigValue(
    linkedThirdPartyConfigurations,
    selectedThirdparty?.type,
    'RelationshipTypeMandatory'
  );
  const isPercentageRequired = getConfigValue(
    linkedThirdPartyConfigurations,
    selectedThirdparty?.type,
    'OwnershipPercentageMandatory'
  );

  const {
    control: controlLinkThirdparty,
    reset: resetLinkThirdparty,
    formState: { isValid },
    handleSubmit: handleLinkThirdpartySubmit,
  } = useForm({
    defaultValues: {
      relationship1to2: null,
      relationship2to1: null,
      percentage: null,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const debouncedSearchParam = useDebounce(watchSearch('searchParam'), 500);

  const isProceedDisabled = !isValid || !selectedThirdparty || linkThirdPartyLoading;

  useEffect(() => {
    if (activeThirdPartyInternalId) {
      setSearchValue('searchParam', activeThirdPartyInternalId);
    }
    // eslint-disable-next-line
  }, [activeThirdPartyInternalId]);

  const handleOnCloseModal = () => {
    resetLinkThirdparty();
    resetThirdpartySearch();
    setThirdpartiesData([]);
    setSelectedThirdparty(null);
    setActiveInternalId(null);
    if (onClose) {
      onClose();
    }
  };

  const onSubmit = async (values: any) => {
    try {
      const response = await linkThirdPartyTrigger({
        urlParams: { selectedThirdPartyId: selectedThirdparty?.thirdpartyId },
        body: JSON.stringify(values),
      });
      if (onLinked && response?.linkedThirdParty2) {
        onLinked(response.linkedThirdParty2);
      }
    } finally {
      handleOnCloseModal();
    }
  };

  const handleOpenCreateThirdparty = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseThirdPartyFormModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleSuccessThirdPartyFormModal = (thirdParty: ThirdPartyCreateModelResponse) => {
    setIsCreateModalOpen(false);
    setActiveInternalId(thirdParty.internalId);
    setSearchValue('searchParam', thirdParty.internalId);
  };

  const handleSelectThirdparty = (thirdparty: ThirdPartyBasicData) => {
    setSelectedThirdparty(thirdparty);
  };

  const getThirdPartyIcon = (type: string) => {
    switch (type) {
      case ThirdPartyType.Individual:
        return <Icon icon={'user'} aria-hidden size="xxxxs" />;
      case ThirdPartyType.Business:
        return <Icon icon={'company'} aria-hidden size="xxxxs" />;
      default:
        return <UnknownTypeIcon aria-hidden />;
    }
  };

  function getRelationshipPlaceholder(mainType: string, selectedType: string) {
    if (mainType === 'Business' && selectedType === 'Individual') return 'e.g. Business';
    if (mainType === 'Individual' && selectedType === 'Business') return 'e.g. Shareholder';
    if (mainType === 'Individual' && selectedType === 'Individual') return 'e.g. Relative';
    if (mainType === 'Business' && selectedType === 'Business') return 'e.g. Subsidiary';
    return 'e.g. Shareholder';
  }

  useEffect(() => {
    setSelectedThirdparty(null);
    resetLinkThirdparty();
    if (debouncedSearchParam) {
      thirdPartySearchTrigger({
        body: JSON.stringify({
          thirdPartyId: mainThirdPartyId,
          page: 1,
          pageSize: 10,
          query: debouncedSearchParam,
          filter: {},
          sort: null,
          showAll: false,
        }),
      });
    } else {
      setThirdpartiesData([]);
    }
    // eslint-disable-next-line
  }, [debouncedSearchParam, thirdPartySearchTrigger]);

  useEffect(() => {
    setActiveInternalId(activeThirdPartyInternalId ?? null);
  }, [activeThirdPartyInternalId]);

  useEffect(() => {
    if (!thirdPartySearchLoading && Array.isArray(thirdPartySearchData)) {
      setThirdpartiesData(thirdPartySearchData);
    }

    if (activeInternalId && thirdPartySearchData && !thirdPartySearchLoading) {
      const match = thirdPartySearchData?.find(
        (tp: any) => String(tp.id).toLowerCase() === String(activeInternalId).toLowerCase()
      );
      if (match) {
        setSelectedThirdparty(match);
      }
    }
  }, [thirdPartySearchData, thirdPartySearchLoading, activeInternalId]);

  const showHelperText =
    (thirdpartiesData.length === 0 &&
      debouncedSearchParam &&
      !selectedThirdparty &&
      !thirdPartySearchLoading) ||
    thirdPartySearchError;

  const showSearchResults =
    thirdpartiesData.length > 0 &&
    !selectedThirdparty &&
    !thirdPartySearchLoading &&
    !activeInternalId;

  const showLinkageTable = selectedThirdparty;

  const handleRelationshipChange = (value: string, onChange: (v: string) => void) => {
    if (value.trim() === '' && value.length > 0) return;
    onChange(value);
  };

  return (
    <Modal
      isOpen={isOpen}
      customOverlayWidth="700px"
      onClose={() => handleOnCloseModal()}
      title={title}
    >
      <ModalContent>
        <ThirdPartySearchContainer>
          {!showLinkageTable && !activeInternalId && (
            <FormControl errorMessage={null}>
              <Input
                label={
                  'Lookup existing ' + (terminologyConfig?.thirdPartySingularNaming + ' name or ID')
                }
                id="searchParam"
                name="searchParam"
                register={registerSearch}
                fullWidth
                valid={isTouchedSearchField?.searchParam}
                required={true}
              />
            </FormControl>
          )}
          {thirdPartySearchLoading && (
            <LoadingContainer>
              <Loading />
            </LoadingContainer>
          )}
          {showSearchResults && (
            <ThirdPartiesSearchResultList
              thirdParties={thirdpartiesData}
              onClick={(thirdparty) => handleSelectThirdparty(thirdparty)}
            />
          )}
        </ThirdPartySearchContainer>
        {showHelperText && (
          <CreateHelperText>
            {'No ' +
              terminologyConfig?.thirdPartySingularNaming +
              ' name or ID matches the input value.  Please verify the input value or create a new ' +
              terminologyConfig?.thirdPartySingularNaming +
              ' if it doesn’t already exist. '}
            <Button text small className="button" onClick={handleOpenCreateThirdparty}>
              Create {terminologyConfig?.thirdPartySingularNaming}
            </Button>
          </CreateHelperText>
        )}

        <ResultsContainer>
          {showLinkageTable && (
            <>
              <Typography value="Relationship details" className="table-section-title" tag="h3" />
              <SimpleTable>
                <thead>
                  <tr>
                    <th>{terminologyConfig?.thirdPartySingularNaming ?? 'Third party'}</th>
                    <th>
                      {'Relationship'}
                      {isRelationshipRequired && <span style={{ color: 'red' }}> *</span>}
                    </th>
                    {isPercentageEnabled && (
                      <th>
                        {'Percentage'}
                        {isPercentageRequired && <span style={{ color: 'red' }}> *</span>}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <LabelIconWrapper>
                        <LabelIcon
                          label={selectedThirdparty?.name}
                          icon={getThirdPartyIcon(selectedThirdparty?.type)}
                        />
                        <Label>to</Label>
                        <LabelIcon
                          label={mainThirdparty.name}
                          icon={getThirdPartyIcon(mainThirdparty.type)}
                        />
                      </LabelIconWrapper>
                    </td>
                    <td>
                      <Controller
                        name="relationship2to1"
                        control={controlLinkThirdparty}
                        render={({ field }) => (
                          <LinkageTableInput
                            value={field.value || ''}
                            id="relationship2to1"
                            placeholder={getRelationshipPlaceholder(
                              selectedThirdparty?.type,
                              mainThirdparty.type
                            )}
                            onChange={(e) =>
                              handleRelationshipChange(e.target.value, field.onChange)
                            }
                          />
                        )}
                      />
                    </td>
                    {isPercentageEnabled && (
                      <td>
                        <Controller
                          name="percentage"
                          control={controlLinkThirdparty}
                          render={({ field }) => (
                            <PercentageInput
                              value={field.value || ''}
                              placeholder="0%"
                              onValueChange={field.onChange}
                              input={LinkageTableInput}
                            />
                          )}
                        />
                      </td>
                    )}
                  </tr>
                  <tr>
                    <td>
                      <LabelIconWrapper>
                        <LabelIcon
                          label={mainThirdparty.name}
                          icon={getThirdPartyIcon(mainThirdparty.type)}
                        />
                        <Label>to</Label>
                        <LabelIcon
                          label={selectedThirdparty?.name}
                          icon={getThirdPartyIcon(selectedThirdparty?.type)}
                        />
                      </LabelIconWrapper>
                    </td>
                    <td>
                      <Controller
                        name="relationship1to2"
                        control={controlLinkThirdparty}
                        render={({ field }) => (
                          <LinkageTableInput
                            value={field.value || ''}
                            id="relationship1to2"
                            placeholder={getRelationshipPlaceholder(
                              mainThirdparty.type,
                              selectedThirdparty?.type
                            )}
                            onChange={(e) =>
                              handleRelationshipChange(e.target.value, field.onChange)
                            }
                          />
                        )}
                      />
                    </td>
                    {isPercentageEnabled && (
                      <td>
                        <Controller
                          name="percentage"
                          control={controlLinkThirdparty}
                          render={({ field }) => (
                            <PercentageInput
                              value={field.value || ''}
                              placeholder="0%"
                              onValueChange={field.onChange}
                              input={LinkageTableInput}
                            />
                          )}
                        />
                      </td>
                    )}
                  </tr>
                </tbody>
              </SimpleTable>
            </>
          )}
        </ResultsContainer>
      </ModalContent>
      <ThirdPartyFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseThirdPartyFormModal}
        onSuccess={handleSuccessThirdPartyFormModal}
        isChild={true}
      />
      <ButtonContainer>
        <Button text className="button" onClick={() => handleOnCloseModal()}>
          Cancel
        </Button>
        <Tooltip
          tooltipText="Please populate all mandatory fields in the above table."
          placement="top"
          tooltipShown={showTooltip && isProceedDisabled}
          bodyStyles="padding: 10px; padding-bottom: 10px;"
        >
          <span
            style={{ display: 'inline-block' }}
            onMouseEnter={() => isProceedDisabled && setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <Button
              secondary
              className="button"
              onClick={handleLinkThirdpartySubmit(onSubmit)}
              disabled={isProceedDisabled}
            >
              {linkThirdPartyLoading ? (
                <Loading />
              ) : activeInternalId ? (
                'Create ' + terminologyConfig?.thirdPartySingularNaming
              ) : (
                'Link ' + terminologyConfig?.thirdPartySingularNaming
              )}
            </Button>
          </span>
        </Tooltip>
      </ButtonContainer>
    </Modal>
  );
};

export default LinkThirdpartyModal;
